import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import Confetti from "react-confetti";
import ColorService, { PUZZLE_GRID_COLORS } from "../../services/ColorService";
import CrosswordScrambleRack from "../puzzles/crossword/components/CrosswordScrambleRack";

const GRID_SIZE = 5;
const HELLO = "HELLO";
const HELLO_ROW = 2;
const HELLO_CELLS = HELLO.split("").map((_, col) => ({
    row: HELLO_ROW,
    col,
    charIndex: col,
}));
const HIGHLIGHT_COLORS = ColorService.getHighlightColors();
const CORRECT_BORDER = "#F9A825";
const BLOCK_COLOR = "#9e9e9e";
const EMPTY_FOUND_KEYS = new Set();
const EMPTY_HINT_CELLS = [];
const CONFETTI_MS = 4500;

/** White crossword cells besides HELLO: a down stem through the E. */
function isBlockCell(row, col) {
    if (row === HELLO_ROW) return false;
    if (col === 1) return false;
    return true;
}

function clueNumber(row, col) {
    if (row === HELLO_ROW && col === 0) return "1";
    if (row === 0 && col === 1) return "2";
    return "";
}

/**
 * Interactive 5×5 crossword figure: HELLO across the middle row, with the
 * same scramble rack used in play so visitors can drop letters into place.
 */
export default function LandingCrosswordSample() {
    const [placed, setPlaced] = useState("");
    const [celebrateByKey, setCelebrateByKey] = useState(null);
    const [confettiActive, setConfettiActive] = useState(false);
    const [frameSize, setFrameSize] = useState({ w: 340, h: 420 });
    const celebrateTimerRef = useRef(null);
    const confettiTimerRef = useRef(null);
    const completedRef = useRef(false);
    const sampleRef = useRef(null);

    useLayoutEffect(() => {
        const el = sampleRef.current;
        if (!el) return undefined;
        const apply = () => {
            setFrameSize({
                w: Math.max(1, el.clientWidth),
                h: Math.max(1, el.clientHeight),
            });
        };
        apply();
        const observer = new ResizeObserver(apply);
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    useEffect(
        () => () => {
            if (celebrateTimerRef.current) window.clearTimeout(celebrateTimerRef.current);
            if (confettiTimerRef.current) window.clearTimeout(confettiTimerRef.current);
        },
        [],
    );

    const handleLettersChange = useCallback((letters) => {
        const next = String(letters || "").toUpperCase();
        setPlaced(next);
        if (next !== HELLO) {
            completedRef.current = false;
            return;
        }
        const celebrate = {};
        HELLO_CELLS.forEach((cell, index) => {
            celebrate[`${cell.row},${cell.col}`] = index;
        });
        setCelebrateByKey(celebrate);
        if (celebrateTimerRef.current) window.clearTimeout(celebrateTimerRef.current);
        celebrateTimerRef.current = window.setTimeout(() => {
            setCelebrateByKey(null);
            celebrateTimerRef.current = null;
        }, 720);
        if (completedRef.current) return;
        completedRef.current = true;
        setConfettiActive(true);
        if (confettiTimerRef.current) window.clearTimeout(confettiTimerRef.current);
        confettiTimerRef.current = window.setTimeout(() => {
            setConfettiActive(false);
            confettiTimerRef.current = null;
        }, CONFETTI_MS);
    }, []);

    const isComplete = placed === HELLO;

    return (
        <div className="pi-xw-demo__sample" ref={sampleRef}>
            <div className="pi-xw-demo__grid" role="img" aria-label="Empty five by five crossword. HELLO appears when every letter is in the right place.">
                {Array.from({ length: GRID_SIZE }, (_, row) =>
                    Array.from({ length: GRID_SIZE }, (_, col) => {
                        const blocked = isBlockCell(row, col);
                        const helloIndex = row === HELLO_ROW ? col : -1;
                        const isHello = helloIndex >= 0;
                        const letter = isHello && isComplete ? HELLO.charAt(helloIndex) : "";
                        const isCorrect = isHello && isComplete;
                        const celebrate = Boolean(celebrateByKey?.[`${row},${col}`] != null);
                        const number = clueNumber(row, col);
                        const bg = blocked
                            ? BLOCK_COLOR
                            : isHello
                              ? HIGHLIGHT_COLORS[helloIndex] || PUZZLE_GRID_COLORS.ORANGE
                              : PUZZLE_GRID_COLORS.WHITE;
                        const classNames = [
                            "pi-xw-demo__cell",
                            blocked ? "pi-xw-demo__cell--block" : "",
                            isHello ? "pi-xw-demo__cell--hello" : "",
                            isCorrect ? "pi-xw-demo__cell--correct" : "",
                            celebrate ? "pi-xw-demo__cell--pop" : "",
                        ]
                            .filter(Boolean)
                            .join(" ");
                        return (
                            <div
                                key={`${row}-${col}`}
                                className={classNames}
                                style={{
                                    backgroundColor: bg,
                                    borderColor: isCorrect ? CORRECT_BORDER : PUZZLE_GRID_COLORS.BORDER_LIGHT,
                                }}
                            >
                                {number ? (
                                    <span className="pi-xw-demo__clue">{number}</span>
                                ) : null}
                                {letter ? <span className="pi-xw-demo__letter">{letter}</span> : null}
                            </div>
                        );
                    }),
                )}
            </div>
            <p className="pi-xw-demo__clue-line">1 Across · HELLO</p>
            <div className="pi-xw-demo__rack">
                <CrosswordScrambleRack
                    wordKey="landing-hello"
                    expectedWord={HELLO}
                    cells={HELLO_CELLS}
                    hintRevealCells={EMPTY_HINT_CELLS}
                    foundCellKeySet={EMPTY_FOUND_KEYS}
                    meaningPreview="A friendly greeting"
                    onLettersChange={handleLettersChange}
                />
            </div>
            {confettiActive ? (
                <div className="pi-xw-demo__confetti" aria-hidden>
                    <Confetti
                        width={frameSize.w}
                        height={frameSize.h}
                        recycle
                        numberOfPieces={280}
                        gravity={0.18}
                        tweenDuration={400}
                        confettiSource={{ x: 0, y: 0, w: frameSize.w, h: 16 }}
                        style={{ pointerEvents: "none" }}
                    />
                </div>
            ) : null}
        </div>
    );
}
